import type { PropsWithChildren, FunctionComponent } from 'react'
import { useCallback, useState, useMemo } from 'react'
import { colors, NewSelect, Text } from '@retailer-platform/shared-components'
import { SelectField } from '@retailer-platform/shared-components/src/tds'
import { useFetchDepartmentsForRetailerQuery } from '../../../__codegen__/api'
import { useDashMessage } from '../../../intl/intl.hooks'

interface Props {
  retailerId: string
  isDisabled?: boolean
  onChange: (selectedDepartmentId: string) => void
  initialValue?: string
}

export const DepartmentsSelect: FunctionComponent<PropsWithChildren<Props>> = ({
  retailerId,
  onChange,
  isDisabled,
  initialValue,
}) => {
  const [departmentId, setDepartmentID] = useState<string>(initialValue)

  const { data, loading, error } = useFetchDepartmentsForRetailerQuery({
    variables: { retailerId },
  })

  const defaultLabel = useDashMessage('components.departmentsSelect.defaultLabel')

  const departments = data?.retailer?.departments

  const departmentsOptions = useMemo(() => {
    if (!departments) return []

    const defaultOption = { label: defaultLabel, value: undefined }
    const departmentOptions = departments.map(dep => ({ label: dep.name, value: dep.id }))

    return [defaultOption, ...departmentOptions]
  }, [departments, defaultLabel])

  const handleOnChange = useCallback(
    (selectedDepartmentId?: string) => {
      setDepartmentID(selectedDepartmentId)
      onChange(selectedDepartmentId)
    },
    [onChange]
  )

  if (departmentsOptions.length > 0) {
    // Mantine select requires value to be defined
    departmentsOptions[0].value = ''
  }

  return (
    <>
      <SelectField
        placeholder={defaultLabel}
        isLoading={loading}
        onChange={handleOnChange}
        options={departmentsOptions}
        value={departmentId}
        searchable={true}
        aria-label="departments-select"
        disabled={isDisabled}
        data-testid="departments-select"
      />
      <Text css={{ color: `${colors.DETRIMENTAL.REGULAR}` }}>
        <>{error}</>
      </Text>
    </>
  )
}
